import { booleanAttribute } from "#ce/utils/props";

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
//
// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store.
export const props = Object.freeze({
  /**
   * Creates a sp-icon component before default content in the prepend slot.
   *
   * @type {String}
   * @default undefined
   */
  prependIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Creates a sp-icon component after default content in the append slot.
   *
   * @type {String}
   * @default undefined
   */
  appendIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Whether to propagate the click event on the append slot.
   *
   * @type {Boolean|String}
   * @default false
   */
  propagateAppendClick: booleanAttribute(),
  /**
   * Removes the ability to click or target the input.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Removes the ability to edit the input’s value.
   *
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * Marks the input `focus-on-open`, so an enclosing sp-dialog focuses it when
   * it opens.
   *
   * @type {Boolean|String}
   * @default false
   */
  focusOnOpen: booleanAttribute(),
  /**
   * Sets the input’s required attribute.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * The message to use for validity errors.
   *
   * @type {String}
   * @default undefined
   */
  customValidity: {
    type: String,
    default: undefined,
  },
  /**
   * Sets the input’s inputmode attribute.
   *
   * @type {String}
   * @default undefined
   */
  inputmode: {
    type: String,
    default: undefined,
    validator: (value) => {
      const validValues = ["none", "text", "decimal", "numeric", "tel", "search", "email", "url"];
      return validValues.includes(value);
    },
  },
  /**
   * Visible label rendered above the field, which also names the inner input.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Guidance rendered below the field while it has focus. `persistent-hint` keeps it visible.
   * A `messages` value takes its place.
   *
   * @type {String}
   * @default undefined
   */
  hint: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the hint visible whether or not the field has focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentHint: booleanAttribute(),
  /**
   * Message or messages rendered below the field, in place of the hint. A single string, or
   * several as an array assigned through the `messages` property.
   *
   * @type {String|String[]}
   * @default undefined
   */
  messages: {
    type: [String, Array],
    default: undefined,
  },
  /**
   * Sets the input’s placeholder text.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * Sets the input’s type attribute.
   *
   * @type {String}
   * @default "text"
   */
  type: {
    type: String,
    default: "text",
  },
  /**
   * Sets the input’s loading state.
   * If true, shows a linear progress bar.
   *
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Allows for the component to be cleared.
   *
   * @type {Boolean|String}
   * @default false
   */
  clearable: booleanAttribute(),
  /**
   * Applied when using clearable and the input is dirty.
   *
   * @type {String}
   * @default "close"
   */
  clearIcon: {
    type: String,
    default: "close-filled",
  },
  clearIconFill: {
    type: String,
    default: "currentColor",
  },
  /**
   * Accessible name of the clear button. Defaults to the translated "Clear"; pass a
   * more specific name where the field's purpose calls for one.
   *
   * @type {String}
   * @default undefined
   */
  clearAccessibleName: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` on the clear button.
   *
   * @type {String}
   * @default undefined
   */
  clearTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Always show the clearable icon when the input is dirty (By default it only shows on hover).
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentClear: booleanAttribute(),
  /**
   * Sets a `data-testid` on the inner `<input>` element.
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  density: {
    type: String,
    validator: (value) => ["default", "compact", "comfortable"].includes(value),
    default: "default",
  },
  /**
   * Compact density on small screens (mobile). A deployment that shrinks its controls on
   * phones turns it on for every element that declares it through `sp_components.yml`.
   *
   * @type {Boolean|String}
   * @default false
   */
  densitysmcompact: booleanAttribute(),
  variant: {
    type: String,
    validator: (value) => ["default", "outlined", "plain"].includes(value),
    default: "default",
  },
  /**
   * Space-separated ids of the elements that label the field, resolved in the tree the
   * element itself sits in. Set it as `aria-labelledby` on the element. Where the browser
   * lacks ARIA element reflection the referenced text is captured at render time, so a
   * label whose text changes afterwards needs `aria-label`.
   *
   * @type {String}
   * @default undefined
   */
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
});
